import { BLOG_INDEX_URL, BLOG_RAW_BASE_URL } from "../config/blog";
import { slugifyTitle } from "./routes";

let blogIndexCache = null;
let blogIndexPromise = null;

const DISPLAY_TERM_OVERRIDES = {
  "Dev Ops": "DevOps",
  Github: "GitHub",
  "Github Actions": "GitHub Actions",
  "Stoc Keeper": "StocKeeper",
  Stockeeper: "StocKeeper",
  Aws: "AWS",
  Sql: "SQL",
  Ssl: "SSL",
  Api: "API",
  "Rest Api": "REST API",
  "Kotlin Dsl": "Kotlin DSL",
  "Groovy Dsl": "Groovy DSL",
};

const FOLDER_TITLE_OVERRIDES = {
  AWS: "AWS",
  Android: "Android",
  DevConcepts: "Dev Concepts",
  DevOps: "DevOps",
  Linux: "Linux",
  SQL: "SQL",
  Spring: "Spring",
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const applyDisplayTermOverrides = (value) =>
  Object.entries(DISPLAY_TERM_OVERRIDES).reduce(
    (formattedName, [rawLabel, displayLabel]) =>
      formattedName.replace(
        new RegExp(
          `\\b${escapeRegExp(rawLabel).replace(/\\s+/g, "\\s+")}\\b`,
          "g",
        ),
        displayLabel,
      ),
    String(value || ""),
  );

export const humanizeName = (name) => {
  const withoutExtension = String(name || "").replace(/\.(md|mdx|pdf)$/i, "");

  const displayName = withoutExtension
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return applyDisplayTermOverrides(displayName);
};

export const getFolderDisplayName = (folderName) =>
  FOLDER_TITLE_OVERRIDES[folderName] || humanizeName(folderName);

const normalizeCategories = (categories) => {
  if (Array.isArray(categories)) {
    return categories.map((category) => String(category).trim()).filter(Boolean);
  }

  return String(categories || "")
    .split(",")
    .map((category) => category.trim())
    .filter(Boolean);
};

const normalizePath = (path) =>
  String(path || "")
    .split("/")
    .filter((part) => part && part !== ".")
    .reduce((parts, part) => {
      if (part === "..") {
        parts.pop();
      } else {
        parts.push(part);
      }

      return parts;
    }, [])
    .join("/");

const encodeGitHubPath = (path) =>
  path.split("/").map(encodeURIComponent).join("/");

export const getBlogPostRawUrl = (path) =>
  `${BLOG_RAW_BASE_URL}/${encodeGitHubPath(path)}`;

const normalizeExtension = (extension, path) => {
  const rawExtension =
    extension ||
    String(path || "").match(/(\.[^./?#]+)(?:[?#].*)?$/)?.[1] ||
    "";

  return String(rawExtension).trim().toLowerCase();
};

export const normalizeBlogIndexEntry = (entry = {}) => {
  const path = normalizePath(entry.path);
  const categories = normalizeCategories(entry.categories);
  const firstPathSegment = path.split("/")[0] || "Uncategorized";
  const fileName = path.split("/").filter(Boolean).pop() || "";
  const folderGroup = getFolderDisplayName(firstPathSegment);
  const categoryLabel = categories[0] || folderGroup;
  const title = String(entry.title || humanizeName(fileName || entry.slug)).trim();
  const slug = slugifyTitle(entry.slug || title);
  const readTime = Number.parseInt(entry.readTime, 10);
  const extension = normalizeExtension(entry.extension, path);

  return {
    title,
    date: String(entry.date || "").trim(),
    slug,
    summary: String(entry.summary || "").trim(),
    categories,
    readTime: Number.isFinite(readTime) && readTime > 0 ? readTime : null,
    path,
    categoryLabel: applyDisplayTermOverrides(categoryLabel),
    folderGroup,
    isNote: entry.isNote === true,
    rawUrl: String(entry.rawUrl || "").trim(),
    type: String(entry.type || "").trim().toLowerCase(),
    extension,
    language: String(entry.language || "").trim().toLowerCase(),
  };
};

export async function loadBlogIndex({ force = false } = {}) {
  if (!force && blogIndexCache) {
    return blogIndexCache;
  }

  if (!force && blogIndexPromise) {
    return blogIndexPromise;
  }

  blogIndexPromise = fetch(BLOG_INDEX_URL, {
    headers: {
      Accept: "application/json",
    },
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(
          `Blog index failed: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("Blog index response is invalid.");
      }

      blogIndexCache = data
        .map(normalizeBlogIndexEntry)
        .filter((post) => post.title && post.slug && post.path);

      return blogIndexCache;
    })
    .finally(() => {
      blogIndexPromise = null;
    });

  return blogIndexPromise;
}

export function clearBlogIndexCache() {
  blogIndexCache = null;
  blogIndexPromise = null;
}
